/**
 * Media Matchers integration — five GET endpoints.
 *
 *   Leads Search   on arrival at the contact step
 *   TCPA Search    same moment; returns consent text + mRecordID
 *   TCPA Post      on contact-form submit
 *   Clicks Search  right after submit, so results are ready for the carousel
 *   Leads Post     once per school the visitor consents to in the carousel
 */
import {
  buildSearchParams,
  applyPublisherParams,
  getMetaCookies,
  getSessionId,
  getTrustedFormUrl,
  resolveSubIds,
  setIf,
  type ContactProfile,
  type SearchProfile,
} from './params'
import {
  parseResults,
  postRequest,
  searchRequest,
  withBrandName,
} from './transport'

const LEADS_SEARCH = 'https://leads.media-matchers.com/search'
const LEADS_POST = 'https://leads.media-matchers.com/post'
const TCPA_SEARCH = 'https://tcpa.media-matchers.com/search'
const TCPA_POST = 'https://tcpa.media-matchers.com/post'
const CLICKS_SEARCH = 'https://api.media-matchers.com/search'

export type MMSchool = Record<string, unknown>

export interface TCPAResult {
  mRecordID: string
  mBrandID: string
  disclosure: string
  raw: unknown
}

function firstString(obj: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const val = obj[key]
    if (typeof val === 'string' && val.trim()) return val.trim()
    if (typeof val === 'number') return String(val)
  }
  return ''
}

/**
 * Leads Search — runs after submit so the full contact profile goes with it.
 * `excludeBrand` carries the mBrandID the TCPA call already matched, so that
 * brand is not offered again in the leads carousel.
 */
export async function leadsSearch(
  profile: SearchProfile,
  contact?: ContactProfile,
  excludeBrand?: string,
): Promise<MMSchool[] | null> {
  const params = buildSearchParams(profile)
  if (contact) {
    setIf(params, 'firstName', contact.firstName)
    setIf(params, 'lastName', contact.lastName)
    setIf(params, 'email', contact.email)
    setIf(params, 'phone', contact.phone)
  }
  setIf(params, 'excludeBrand', excludeBrand || '')
  const data = await searchRequest('LeadsSearch', LEADS_SEARCH, params)
  if (data === null) return null
  return withBrandName(parseResults(data))
}

/** TCPA Search — supplies the consent language we must display. */
export async function tcpaSearch(
  profile: SearchProfile,
): Promise<TCPAResult | null> {
  const params = buildSearchParams(profile)
  const data = await searchRequest('TCPASearch', TCPA_SEARCH, params)
  if (data === null) return null
  const results = parseResults(data)
  const record =
    results[0] ||
    ((data && typeof data === 'object' ? data : {}) as Record<string, unknown>)
  const mRecordID = firstString(record, ['mRecordID', 'recordID', 'mRecordId'])
  if (!mRecordID) {
    // A reachable endpoint that matched nothing is NOT a transport failure —
    // log the shape so an empty match can be told apart from a broken call.
    console.warn(
      '[MM:TCPASearch] No mRecordID in response.',
      'results:',
      results.length,
      'top-level keys:',
      data && typeof data === 'object' ? Object.keys(data) : typeof data,
      'payload:',
      data,
    )
    return null
  }
  return {
    mRecordID,
    mBrandID: firstString(record, ['mBrandID', 'brandID', 'mBrandId']),
    // tcpaDisclaimer is the documented field; the rest are defensive.
    disclosure: firstString(record, [
      'tcpaDisclaimer',
      'mTCPADisclaimer',
      'mDisclosure',
      'disclosure',
      'tcpaDisclosure',
      'mTCPADisclosure',
      'consentText',
      'mConsentText',
    ]),
    raw: data,
  }
}

/** TCPA Post — fired on submit, before the Clicks Search. */
export function tcpaPost(
  mRecordID: string,
  contact: ContactProfile,
): Promise<boolean> {
  const params = new URLSearchParams()
  const subs = resolveSubIds()
  const { fbc, fbp } = getMetaCookies()
  setIf(params, 'mRecordID', mRecordID)
  setIf(params, 'firstName', contact.firstName)
  setIf(params, 'lastName', contact.lastName)
  setIf(params, 'email', contact.email)
  setIf(params, 'phone', contact.phone)
  setIf(params, 'sub1', subs.sub1)
  setIf(params, 'sub2', subs.sub2)
  setIf(params, 'sub3', subs.sub3)
  setIf(params, 'trustedFormUrl', getTrustedFormUrl())
  params.set('mSessionID', getSessionId())
  setIf(params, 'fbp', fbp)
  setIf(params, 'fbc', fbc)
  applyPublisherParams(params)
  return postRequest('TCPAPost', TCPA_POST, params)
}

/**
 * Clicks Search — the shared payload plus contact fields, excluding the brand
 * that already took the TCPA consent so it isn't offered twice.
 */
export async function clicksSearch(
  profile: SearchProfile,
  contact: ContactProfile,
  excludeBrand?: string,
): Promise<MMSchool[] | null> {
  const params = buildSearchParams(profile)
  setIf(params, 'firstName', contact.firstName)
  setIf(params, 'lastName', contact.lastName)
  setIf(params, 'email', contact.email)
  setIf(params, 'phone', contact.phone)
  setIf(params, 'excludeBrand', excludeBrand)
  const data = await searchRequest('ClicksSearch', CLICKS_SEARCH, params)
  if (data === null) return null
  return withBrandName(parseResults(data))
}

/** The record id a Leads Post needs, under whichever key the school carries. */
export function getSchoolRecordId(school: MMSchool): string {
  return firstString(school, [
    'mRecordID',
    'recordID',
    'mRecordId',
    'mBrandID',
    'brandID',
    'mBrandId',
  ])
}

/** Leads Post — one per school the visitor consents to in the carousel. */
export function leadsPost(
  school: MMSchool,
  contact: ContactProfile,
): Promise<boolean> {
  const params = new URLSearchParams()
  const subs = resolveSubIds()
  const { fbc, fbp } = getMetaCookies()
  setIf(params, 'mRecordID', getSchoolRecordId(school))
  setIf(params, 'firstName', contact.firstName)
  setIf(params, 'lastName', contact.lastName)
  setIf(params, 'email', contact.email)
  setIf(params, 'phone', contact.phone)
  setIf(params, 'sub1', subs.sub1)
  setIf(params, 'sub2', subs.sub2)
  setIf(params, 'sub3', subs.sub3)
  setIf(params, 'trustedFormUrl', getTrustedFormUrl())
  params.set('mSessionID', getSessionId())
  setIf(params, 'fbp', fbp)
  setIf(params, 'fbc', fbc)
  applyPublisherParams(params)
  return postRequest('LeadsPost', LEADS_POST, params)
}

export { getBrandName } from './transport'
export {
  areaFromProgram,
  decodeHighestEducation,
  decodeStartTimeframe,
  mapMilitaryAffiliation,
  normalizeGradYear,
  normalizeState,
  normalizeZip,
} from './params'
export type { ContactProfile, SearchProfile } from './params'
